<script setup>
import { reactive, computed, watch, onMounted } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import './vueDatePickerTheme.css'

const emit = defineEmits(['update:date', 'dateSelected']);

const props = defineProps({
    date: {
        type: [Date, Array],
        default: () => new Date()
    },
    showTime: {
        type: Boolean,
        required: false,
        default: false
    },
    rangeMode: {
        type: Boolean,
        required: false,
        default: false
    },
    actionRow: {
        type: Object,
        required: false,
        default: {
            showNow: false,
            showPreview: false,
            showSelect: true,
            showCancel: false,
        }
    },
    minDate: {
        type: Date,
        required: false,
        default: () => new Date()
    },
    minTime: {
        type: Date,
        required: false,
        default: () => new Date()
    },
    showInline: {
        type: Boolean,
        required: false,
        default: true
    },
    minutesIncrement: {
        type: Number,
        required: false,
        default: 1
    },
    // Lets the user type the date instead of only picking it; has no effect inline, where there is no input
    textInput: {
        type: Boolean,
        required: false,
        default: false
    }
})

const state = reactive({
    date: null,
    actionRow: {
        showNow: props.actionRow.showNow,
        showPreview: props.actionRow.showPreview,
        showSelect: props.actionRow.showSelect,
        showCancel: props.actionRow.showCancel,
    }
})

const format = computed(() => {
    if (props.showTime) {
        return 'yyyy-MM-dd HH:mm';
    }

    return 'yyyy-MM-dd';
})

// Each pattern is tried in turn. The picker trims a pattern to the typed length, so '2026-10-21' and '20261021' both work.
const textInputOptions = computed(() => {
    if (!props.textInput) {
        return false;
    }

    return {
        format: props.showTime
            ? ['yyyy-MM-dd HH:mm', 'yyyyMMdd HHmm']
            : ['yyyy-MM-dd', 'yyyyMMdd']
    };
})

const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const formatDateTime = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const handleDate = (newValue) => {
    emit('update:date', newValue);
    emit('dateSelected');

    // Clearing the field emits null, and range mode emits an array, neither of which can be formatted
    if (newValue instanceof Date) {
        emit('dateSelectedFormatted', formatDate(newValue));
        emit('dateTimeSelectedFormatted', formatDateTime(newValue));
    }

    setTimeout(function(){
        state.actionRow.showSelect = false;
        state.actionRow.showCancel = false;
    }, 100)
}

const handleInternalChange = (newValue) => {
    if (props.rangeMode) {
        state.actionRow.showSelect = true;
        state.actionRow.showCancel = true;
    }
}

watch(state, (newValue) => {
    emit('update:date', newValue.date);
})

watch(props, (newValue) => {
    state.date = newValue.date
}, {deep: true})


onMounted(async () => {
    if (props.rangeMode && !Array.isArray(props.date)) {
        state.date = [props.date, props.date];
        return;
    }

    state.date = props.date;
})

</script>
<template>
    <VueDatePicker
        v-model="state.date"
        :inline="props.showInline"
        :range="props.rangeMode"
        :auto-apply="true"
        :enable-time-picker="props.showTime"
        :time-picker-inline="props.showTime"
        :month-change-on-scroll="false"
        :minutes-increment="minutesIncrement"
        :no-hours-overlay="true"
        :no-minutes-overlay="true"
        :min-date="props.minDate"
        :format="format"
        :text-input="textInputOptions"
        locale="en-US"
        :select-text="_mft('shared:select')"
        :action-row="state.actionRow"
        @update:model-value="handleDate"
        @internal-model-change="handleInternalChange"
        :no-today="true"
        class="font-body"
    ></VueDatePicker>
</template>
